/* eslint @typescript-eslint/no-use-before-define: "off" */
import React, { useState, useLayoutEffect, useEffect, useCallback, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Decimal } from 'decimal.js';
import { useSelector } from 'react-redux';
import { Button } from '@qga/roo-ui/components';
import theme from 'lib/theme';
import { useIsAuthenticated } from 'lib/oauth';
import { PAYMENT_METHODS } from 'lib/enums/payment';
import { useDataLayer } from 'hooks/useDataLayer';
import usePointsConverters from 'hooks/points/usePointsConverters';
import { getPointsBalance } from 'store/user/userSelectors';
import { getLuxePointsLevels, getLuxeInstanceId } from 'store/pointsBurnLuxe/pointsBurnLuxeSelectors';
import { getIsExclusive } from 'store/property/propertySelectors';
import { useLoginUrl } from 'lib/qffAuth';

import type { UrlWithParsedQuery } from 'url';

const MIN_POINTS = 5000;
const CSS_VARIABLE_SLIDER_PROGRESS = '--slider-progress';
const CASH_FORMATTER = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const POINTS_FORMATTER = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 });
const SHOULD_SLIDER_SNAP = true;

type OfferPointsPaySliderUpdatedProps = {
  maxPoints: number;
  maxCash: number;
  offerId: string;
  offerInstanceId: string;
  offerName: string;
  roomName: string;
  roomTypeId: string;
  propertyId: string;
  onCashChange?: ({ cashAmount }: { cashAmount: Decimal }) => void;
};

const OfferPointsPaySliderUpdated = ({
  maxCash,
  maxPoints,
  offerId,
  offerInstanceId,
  offerName,
  roomName,
  roomTypeId,
  propertyId,
  onCashChange,
}: OfferPointsPaySliderUpdatedProps) => {
  const router = useRouter();

  const isAuthenticated = useIsAuthenticated();
  const { loginUrl } = useLoginUrl();
  const pointsBalance = useSelector(getPointsBalance);

  const luxeTiers = useSelector(getLuxePointsLevels);
  const useCurrentTiers = usePointsConverters();
  const useLuxeTiers = usePointsConverters(luxeTiers);

  const isExclusive = useSelector(getIsExclusive);
  const luxeTierInstanceId = useSelector(getLuxeInstanceId);
  const isLuxeOffer = offerInstanceId === luxeTierInstanceId || isExclusive;

  const { convertCashToPoints, convertPointsToCash } = isLuxeOffer ? useLuxeTiers : useCurrentTiers;

  const [value, setValue] = useState({ cash: CASH_FORMATTER.format(maxCash), points: '0' });
  const minPointsCashValue = maxCash - convertPointsToCash({ points: MIN_POINTS }).toNumber();

  const sliderRef = useRef<HTMLInputElement>(null!);
  // handle "progress" styling for webkit based browsers
  useLayoutEffect(() => {
    if (/AppleWebKit\/(\S+)/.test(window.navigator.userAgent)) {
      const percent = (parseFormattedValue(value.cash) / (parseFloat(sliderRef.current.max) - parseFloat(sliderRef.current.min))) * 100;
      sliderRef.current.style.setProperty(CSS_VARIABLE_SLIDER_PROGRESS, `${percent}%`);
    }
  }, [value.cash]);

  useEffect(() => {
    if (typeof onCashChange === 'function') onCashChange({ cashAmount: new Decimal(maxCash) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // #region input handlers
  const handleSliderInput: React.FormEventHandler<HTMLInputElement> = (e) => {
    const initialCashValue = parseFormattedValue(e.currentTarget.value);
    const snappedCashValue = SHOULD_SLIDER_SNAP
      ? calcSnap(
          initialCashValue,
          minPointsCashValue,
          maxCash,
          maxCash - initialCashValue < (maxCash - minPointsCashValue) / 2 ? maxCash : minPointsCashValue,
        )
      : initialCashValue;
    const cashValue = limitWithinRange(snappedCashValue, 0, maxCash);
    const pointsValue = convertCashToPoints({ cash: maxCash - cashValue }).toNumber();
    setValue({ cash: CASH_FORMATTER.format(cashValue), points: POINTS_FORMATTER.format(pointsValue) });
  };

  const handleSliderPointerUp: React.PointerEventHandler<HTMLInputElement> = (e) => {
    if (SHOULD_SLIDER_SNAP && typeof onCashChange === 'function') onCashChange({ cashAmount: new Decimal(e.currentTarget.value) });
    else if (SHOULD_SLIDER_SNAP) return;

    const initialCashValue = parseFormattedValue(e.currentTarget.value);
    const cashValue = limitWithinRange(calcSnap(initialCashValue, minPointsCashValue, maxCash, minPointsCashValue), 0, maxCash);
    const pointsValue = convertCashToPoints({ cash: maxCash - cashValue }).toNumber();

    if (typeof onCashChange === 'function') onCashChange({ cashAmount: new Decimal(cashValue) });
    setValue({ cash: CASH_FORMATTER.format(cashValue), points: POINTS_FORMATTER.format(pointsValue) });
  };

  const handlePointsChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const points = e.currentTarget.value;
    const cashValue = maxCash - convertPointsToCash({ points: parseFormattedValue(points) }).toNumber();
    setValue({ cash: CASH_FORMATTER.format(cashValue), points });
  };

  const handlePointsFinalise = (e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) => {
    const initialPointsValue = parseFormattedValue(e.currentTarget.value);
    const pointsValue = limitWithinRange(calcSnap(initialPointsValue, 0, MIN_POINTS, MIN_POINTS), 0, maxPoints);
    const cashValue = maxCash - convertPointsToCash({ points: pointsValue }).toNumber();

    if (typeof onCashChange === 'function') onCashChange({ cashAmount: new Decimal(cashValue) });
    setValue({ cash: CASH_FORMATTER.format(cashValue), points: POINTS_FORMATTER.format(pointsValue) });
  };

  const handlePointsBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    handlePointsFinalise(e);
  };

  const handlePointsOnKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === '13') handlePointsFinalise(e);
  };

  const handleCashChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const cash = e.currentTarget.value;
    const pointsValue = convertCashToPoints({ cash: maxCash - parseFormattedValue(cash) }).toNumber();
    setValue({ cash, points: POINTS_FORMATTER.format(pointsValue) });
  };

  const handleCashFinalise = (e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) => {
    const initialCashValue = parseFormattedValue(e.currentTarget.value);
    const cashValue = limitWithinRange(calcSnap(initialCashValue, minPointsCashValue, maxCash, minPointsCashValue), 0, maxCash);
    const pointsValue = convertCashToPoints({ cash: maxCash - cashValue }).toNumber();

    if (typeof onCashChange === 'function') onCashChange({ cashAmount: new Decimal(cashValue) });
    setValue({ cash: CASH_FORMATTER.format(cashValue), points: POINTS_FORMATTER.format(pointsValue) });
  };

  const handleCashBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    handleCashFinalise(e);
  };

  const handleCashOnKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === '13') handleCashFinalise(e);
  };
  // #endregion

  const { emitInteractionEvent } = useDataLayer();
  const handleLinkClick = useCallback(() => {
    emitInteractionEvent({ type: 'Select Offer Button', value: `${offerName}` });
  }, [emitInteractionEvent, offerName]);

  const href = useMemo(() => {
    const initialCash = parseFormattedValue(value.cash);
    const { checkIn, checkOut, adults, children, infants, travelType } = router.query;
    const query: UrlWithParsedQuery['query'] = {
      roomTypeId,
      offerId,
      propertyId,
      checkIn,
      checkOut,
      adults,
      children,
      infants,
      travelType,
      payWith: initialCash === maxCash ? PAYMENT_METHODS.CASH : PAYMENT_METHODS.POINTS,
      exclusiveOffer: 'false',
    };
    if (initialCash < maxCash && initialCash > 0) query.initialCash = initialCash.toString();
    return { pathname: '/checkout', query };
  }, [router.query, value.cash, offerId, roomTypeId, propertyId, maxCash]);

  const setPointsToCurrentBalance = () => {
    const points = pointsBalance.toNumber();
    const pointsValue = limitWithinRange(calcSnap(points, 0, MIN_POINTS, 0), 0, maxPoints);
    const cashValue = maxCash - convertPointsToCash({ points: pointsValue }).toNumber();

    if (typeof onCashChange === 'function') onCashChange({ cashAmount: new Decimal(cashValue) });
    setValue({ cash: CASH_FORMATTER.format(cashValue), points: POINTS_FORMATTER.format(pointsValue) });
  };

  useEffect(() => {
    setValue({ cash: CASH_FORMATTER.format(maxCash), points: '0' });
  }, [router.query, maxCash]);

  return (
    <Container style={{ position: 'relative' }}>
      <SliderSection>
        <Heading>Points Plus Pay</Heading>
        <InputsContainer>
          <InputLabel>
            PTS
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              required
              value={value.points}
              onChange={handlePointsChange}
              onBlur={handlePointsBlur}
              onKeyUp={handlePointsOnKeyUp}
              aria-label="Amount in Qantas Points (PTS)"
            />
          </InputLabel>
          <InputDeliminator>+</InputDeliminator>
          <InputLabel>
            $
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              required
              value={value.cash}
              onChange={handleCashChange}
              onBlur={handleCashBlur}
              onKeyUp={handleCashOnKeyUp}
              aria-label="Amount in cash (AUD)"
            />
          </InputLabel>
        </InputsContainer>
        <Slider
          type="range"
          min={0}
          max={maxCash}
          step={0.01}
          value={parseFormattedValue(value.cash)}
          onInput={handleSliderInput}
          onPointerUp={handleSliderPointerUp}
          list="my-detents"
          ref={sliderRef}
        />
        <datalist id="my-detents">
          <option value="0.00" />
          <option value={removeDeliminators(CASH_FORMATTER.format(minPointsCashValue))} />
          <option value={maxCash} />
        </datalist>
        {isAuthenticated ? (
          <Text>
            <InlineButton onClick={setPointsToCurrentBalance}>Set slider</InlineButton> to your balance of{' '}
            {POINTS_FORMATTER.format(pointsBalance.toNumber())} PTS. Minimum 5,000 PTS required.
          </Text>
        ) : (
          <Text>
            <Link passHref={true} href={loginUrl}>
              <InlineLink>Log in</InlineLink>
            </Link>{' '}
            to see your Qantas Points balance. Use min 5,000 points
          </Text>
        )}
      </SliderSection>
      <Link href={href} passHref>
        <CallToAction as="a" onClick={handleLinkClick} aria-label={`Select ${roomName}, ${offerName}`} variant="primary">
          Select
        </CallToAction>
      </Link>
    </Container>
  );
};

export default OfferPointsPaySliderUpdated;

// #region utilities
const removeDeliminators = (value: string) => value.replace(/,/g, '');
const parseFormattedValue = (value: string) => parseFloat(removeDeliminators(value)) || 0;
const limitWithinRange = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const calcSnap = (value: number, floor: number, ceil: number, snapTo: number) => (floor < value && value < ceil ? snapTo : value);
// #endregion

// #region styles
const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',

  backgroundColor: '#f6f6f6',
  padding: '1.5rem 1rem',
});

const SliderSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '.5rem',
});

const Heading = styled.div({
  color: '#000',
  fontSize: theme.fontSizes.md,
  fontWeight: theme.fontWeights.bold,
  lineHeight: theme.lineHeights.default,
  margin: 0,
  marginBottom: '.25rem',
});

const InputsContainer = styled.div({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: '.5rem',
});

const inputText = css`
  color: ${theme.colors.greys.charcoal};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.bold};
  font-feature-settings:
    'clig' off,
    'liga' off;
`;

const InputDeliminator = styled.span`
  user-select: none;
  ${inputText}
`;

const InputLabel = styled.label`
  display: flex;
  height: 2.5rem;
  padding-inline: 0.5rem;
  align-items: center;
  gap: 2px;
  flex: 1 0 0;
  background: ${theme.colors.white};

  color: ${theme.colors.greys.steel};
  font-size: 0.75rem;
  line-height: 1;

  cursor: text;
  &:focus-within {
    outline: ${theme.colors.brand.secondary} auto 1px;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  outline: none;

  text-align: end;
  ${inputText}
`;

const Slider = styled.input({
  '--thumb-height': '1.125rem',
  '--track-height': '.25rem',
  '--track-color': '#d9d9d9',
  width: '100%',
  height: 'var(--thumb-height)',
  color: theme.colors.brand.primary,
  position: 'relative',
  background: 'transparent',
  touchAction: 'none',
  '&:active': {
    cursor: 'grabbing',
  },
  '&:focus-visible': {
    outline: `${theme.colors.brand.secondary} auto 1px`,
  },
  '&, &::-webkit-slider-runnable-track, &::-webkit-slider-thumb': {
    appearance: 'none',
  },
  '&::-webkit-slider-thumb': {
    width: 'var(--thumb-height)',
    height: 'var(--thumb-height)',
    background: 'currentColor',
    borderRadius: 'var(--thumb-height)',
    marginTop: 'calc(calc(var(--track-height) / 2) - calc(var(--thumb-height) /2))',
    cursor: 'grab',
  },
  '&:active::-webkit-slider-thumb': {
    cursor: 'grabbing',
  },
  '&::-webkit-slider-runnable-track': {
    width: '100%',
    height: 'calc(var(--track-height) + 1px)',
    background: `linear-gradient(to right, currentColor, currentColor var(${CSS_VARIABLE_SLIDER_PROGRESS}, 0%), var(--track-color) var(${CSS_VARIABLE_SLIDER_PROGRESS}, 0%))`,
    borderRadius: 'var(--track-height)',
  },
  '&, &::-moz-range-track, &::-moz-range-thumb': {
    appearance: 'none',
    height: 'var(--thumb-height)',
  },
  '&::-moz-range-track, &::-moz-range-thumb, &::-moz-range-progress': {
    background: '#fff0',
  },
  '&::-moz-range-thumb': {
    border: '0',
    width: 'var(--thumb-width, var(--thumb-height))',
    background: 'currentColor',
    borderRadius: 'var(--thumb-height)',
    cursor: 'grab',
  },
  '&:active::-moz-range-thumb': { cursor: 'grabbing' },
  '&::-moz-range-track': {
    width: '100%',
    background: 'var(--track-color)',
  },
  '&::-moz-range-progress': {
    appearance: 'none',
    background: 'currentColor',
  },
  '&::-moz-range-track, &::-moz-range-progress': {
    height: 'calc(var(--track-height) + 1px)',
    borderRadius: 'var(--track-height)',
  },
});

const Text = styled.p({
  margin: 0,
  color: theme.colors.greys.steel,
  fontSize: theme.fontSizes.xs,
});

const inlineInteraction = {
  color: theme.colors.brand.primary,
  textDecoration: 'underline',
  '&:focus-visible': {
    outline: `${theme.colors.brand.secondary} auto 1px`,
  },
};

const InlineButton = styled.button({ appearance: 'none', background: 'transparent', border: 0, padding: 0, ...inlineInteraction });
const InlineLink = styled.a(inlineInteraction);

const CallToAction = styled(Button)`
  padding: 0;
  border: 0;

  font-size: ${theme.fontSizes.sm};
  line-height: 44px;

  &:hover {
    color: ${theme.colors.white};
  }

  &:focus {
    outline: none;
    box-shadow: unset;
  }

  &:focus-visible {
    outline: ${theme.colors.brand.secondary} auto 1px;
  }
`;
// #endregion
